import { lowerCase } from 'es-toolkit';

import { enqueueInfo, enqueueWarning } from '@paragon/notification-tools';
import { getSpinnersToDisplay } from '@paragon/task-spinner';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getPickingObjectInfo, isPickingActive, isPickingModeEasilyCancellable } from '../selectors/pickingSelectors';
import { requireConfirmationToCancelInsertion } from '../selectors/requireConfirmationToCancelInsertion';
import { getTrussEnvelopeInsertionInformation } from '../selectors/trussEnvelopeInsertionInformationSelectors';
import { inViewOnlyMode } from '../selectors/viewOnlyModeSelectors';
import {
  applicationOnPickingLayoutLine,
  clearPickingObject,
  resetInsertionTrussEnvelope,
  resetWorkLineGesture,
  setDimensionMode,
  toggleMemberCuttingMode,
} from '../slices/applicationSlice';
import { layoutSlice } from '../slices/layoutSlice';
import { setMatchFasciaHeightsPanelClosed, setOptimizationPanelClosed } from '../slices/panelDataSlice';
import { snappingUnderlaySlice } from '../slices/snappingUnderlaySlice';
import { setShowReferenceLines, setVisibleLoads } from '../slices/visualOptionsSlice';
import { resetView } from './cameraActions';
import { cancelInsertion } from './insertActions';
import { backUpBearingEnvelopeInsertionPointSelection } from './layout/bearingEnvelopeActions';
import { cancelTransformation } from './layout/transformationActions';
import { finishRenamingDesignsAssociatedWithTrussEnvelopes } from './layout/trussEnvelopeActions';
import { upgradeAndAnalyzeActiveComponentDesign } from './paragonProcessActions';
import { showConfirmCancelInsertionDialog, showInsertionMenuDialog } from './popupActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const runEscapeHotkey = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  if (state.application.inMemberCuttingMode === 'sticky') {
    dispatch(toggleMemberCuttingMode(false));
    if (isPickingModeEasilyCancellable(state)) dispatch(clearPickingObject());
    return;
  }

  if (state.application.insertingObjectInfo?.type === 'bearingEnvelope') {
    const resetOccurred = dispatch(backUpBearingEnvelopeInsertionPointSelection());
    if (resetOccurred) return;
  }

  if (state.snappingUnderlay.scalePicking) {
    dispatch(clearPickingObject());
    dispatch(snappingUnderlaySlice.actions.scalePickingCleared());
    return;
  }

  if (isPickingActive(state) && isPickingModeEasilyCancellable(state)) {
    dispatch(clearPickingObject());
    return;
  }

  if (state.application.inDimensionMode) {
    dispatch(setDimensionMode(false));
    return;
  }

  if (state.panelData.renameLayoutComponentsPanel.open) {
    dispatch(finishRenamingDesignsAssociatedWithTrussEnvelopes());
    return;
  }

  if (state.application.transformingObjectsInfo) {
    dispatch(cancelTransformation());
    return;
  }

  const trussEnvelopeInsertion = getTrussEnvelopeInsertionInformation(state);
  if (trussEnvelopeInsertion?.data.leftPoint && trussEnvelopeInsertion?.data.rightPoint) {
    dispatch(resetInsertionTrussEnvelope());
    return;
  }

  if (state.visualOptions.showReferenceLines) {
    dispatch(setShowReferenceLines(false));
    return;
  }

  if (Object.keys(state.visualOptions.visibleLoadGuids).length > 0) {
    dispatch(setVisibleLoads([]));
    return;
  }

  if (
    state.application.insertingObjectInfo?.type === 'workLine' &&
    state.application.insertingObjectInfo.draft.phase !== 'idle'
  ) {
    dispatch(resetWorkLineGesture());
    return;
  }

  if (state.application.insertingObjectInfo) {
    if (!requireConfirmationToCancelInsertion(state)) {
      dispatch(cancelInsertion());
      return;
    }

    // If we're inserting a component design and something besides the component design is selected, don't cancel
    // insertion, proceed with the next case of deselecting the item.
    if (
      state.application.insertingObjectInfo.type !== 'componentDesign' ||
      getSelectedItemIdentifiers(state).some((item) => item.type === 'componentDesign')
    ) {
      dispatch(showConfirmCancelInsertionDialog());
      return;
    }
  }

  if (state.panelData.matchFasciaHeightsPanelOpen) {
    dispatch(setMatchFasciaHeightsPanelClosed());
    return;
  }

  if (state.panelData.optimizationPanelOpen) {
    dispatch(setOptimizationPanelClosed());
    return;
  }

  dispatch(resetView());
};

export const runParagonProcessHotkey = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  const activeComponent = getActiveComponentDesign(state);
  if (!activeComponent) {
    dispatch(enqueueWarning("Can't analyze—no active component design."));
    return;
  }

  // Counting task spinners is currently the best way to make sure analysis won't conflict with active tasks.
  const activeTaskSpinners = getSpinnersToDisplay(state);
  if (activeTaskSpinners.size > 0) {
    dispatch(enqueueWarning("Can't analyze—tasks are already running."));
    return;
  }

  await dispatch(upgradeAndAnalyzeActiveComponentDesign());
};

export const runInsertHotkey = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const activeTaskSpinners = getSpinnersToDisplay(state);
  if (activeTaskSpinners.size > 0) {
    dispatch(enqueueWarning("Can't insert—tasks are already running."));
    return;
  }

  const insertingObjectType = state.application.insertingObjectInfo?.type;
  const isInsertingObject = insertingObjectType != null;
  if (isInsertingObject) {
    const { deictic, term = lowerCase(insertingObjectType) } = (() => {
      switch (insertingObjectType) {
        case 'ceilingPlane':
        case 'cornerSet':
        case 'roofPlane':
        case 'bearing':
        case 'bearingEnvelope':
        case 'ceilingTray':
        case 'featureArea':
        case 'featurePatternContainer':
        case 'gusset':
        case 'load':
        case 'loadCase':
        case 'member':
        case 'platePair':
        case 'referenceLine':
        case 'splice':
        case 'trussEnvelope':
        case 'memberStack':
        case 'trussEnvelopeRun':
        case 'trussGroup':
          return { deictic: 'this' };

        case 'droppedTopChords':
          return { deictic: 'these' };

        case 'componentDesign':
          return { deictic: 'this', term: 'component' };

        case 'jackGirderConnection':
          return { deictic: 'this', term: 'carried component connection' };

        case 'workLine':
          return { deictic: 'this', term: 'work line' };

        case 'movingLoadSettings':
          return { deictic: 'this', term: 'moving load' };

        case 'parapetLoadSettings':
          return { deictic: 'these', term: 'PAL loads' };

        default:
          assertExhaustiveSwitch(insertingObjectType);
      }
    })();
    dispatch(enqueueInfo(`To insert something else, first finish or cancel inserting ${deictic} ${term}.`));
    return;
  }

  if (inViewOnlyMode(state)) return;

  dispatch(showInsertionMenuDialog());
};

export const TOGGLE_LOCK_TO_BASE_HOTKEY = 'b';

export const runToggleLockToBaseHotkey = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const insertion = getTrussEnvelopeInsertionInformation(state);
  if (!insertion) return;

  const lockedTarget = insertion.settings.snapping.lockedTarget;
  if (lockedTarget) {
    dispatch(layoutSlice.actions.trussEnvelopeSnappingTargetLocked(null));
    return;
  }

  const pickingObjectInfo = getPickingObjectInfo(state);
  const target = 'parallel-snapping-base-line';
  const isPicking = pickingObjectInfo?.type === 'pickingLayoutLine';
  const isPickingTarget = isPicking && pickingObjectInfo.target === target;

  if (isPickingTarget) dispatch(clearPickingObject());
  else dispatch(applicationOnPickingLayoutLine({ target }));
};
